/**
 * API client for Domain Packs endpoints
 */

import apiClient from '../api';
import type {
  DomainPackSummary,
  DomainPackDetail,
  DomainPackStatistics,
  GenerateDomainPackRequest,
  GenerateDomainPackResponse,
  ResearchProgress,
  ApplyDomainPackRequest,
  ApplyDomainPackResponse,
  UpdateHypothesisTestRequest,
  DomainPackStatus,
} from '../../types/domainPack';

const BASE_URL = '/domain-packs';

/**
 * List all domain packs with optional filtering
 */
export const listDomainPacks = async (
  status?: DomainPackStatus,
  skip: number = 0,
  limit: number = 50
): Promise<DomainPackSummary[]> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());

  const response = await apiClient.get<DomainPackSummary[]>(
    `${BASE_URL}?${params.toString()}`
  );
  return response.data;
};

/**
 * Get detailed domain pack with all research components
 */
export const getDomainPack = async (
  domainPackId: string
): Promise<DomainPackDetail> => {
  const response = await apiClient.get<DomainPackDetail>(
    `${BASE_URL}/${domainPackId}`
  );
  return response.data;
};

/**
 * Generate new domain pack through AI research
 * Returns job_id for progress tracking
 */
export const generateDomainPack = async (
  request: GenerateDomainPackRequest
): Promise<GenerateDomainPackResponse> => {
  const response = await apiClient.post<GenerateDomainPackResponse>(
    `${BASE_URL}/generate`,
    request
  );
  return response.data;
};

/**
 * Get real-time progress for research job
 */
export const getResearchProgress = async (
  jobId: string
): Promise<ResearchProgress> => {
  const response = await apiClient.get<ResearchProgress>(
    `${BASE_URL}/jobs/${jobId}/progress`
  );
  return response.data;
};

/**
 * Update domain pack metadata
 */
export const updateDomainPack = async (
  domainPackId: string,
  updates: {
    description?: string;
    status?: DomainPackStatus;
    quality_score?: number;
  }
): Promise<{ message: string }> => {
  const response = await apiClient.patch<{ message: string }>(
    `${BASE_URL}/${domainPackId}`,
    updates
  );
  return response.data;
};

/**
 * Add subdomain to existing domain pack
 */
export const addSubdomain = async (
  domainPackId: string,
  subdomainName: string,
  description?: string,
  scope?: string,
  runResearch: boolean = true
): Promise<{ subdomain_id: string; message: string; job_id?: string; estimated_time?: string }> => {
  const response = await apiClient.post<{
    subdomain_id: string;
    message: string;
    job_id?: string;
    estimated_time?: string;
  }>(`${BASE_URL}/${domainPackId}/subdomains`, {
    subdomain_name: subdomainName,
    description,
    scope,
    run_research: runResearch,
  });
  return response.data;
};

/**
 * Apply domain pack to case (v2.0 copy pattern)
 */
export const applyDomainPackToCase = async (
  request: ApplyDomainPackRequest
): Promise<ApplyDomainPackResponse> => {
  const response = await apiClient.post<ApplyDomainPackResponse>(
    `${BASE_URL}/apply-to-case`,
    request
  );
  return response.data;
};

/**
 * Get all hypotheses for a case
 */
export const getCaseHypotheses = async (caseId: string): Promise<any> => {
  const response = await apiClient.get(`${BASE_URL}/cases/${caseId}/hypotheses`);
  return response.data;
};

/**
 * Update hypothesis test results (triggers learning loop)
 */
export const updateHypothesisTestResults = async (
  hypothesisId: string,
  update: UpdateHypothesisTestRequest
): Promise<{ message: string; learning_loop: string }> => {
  const response = await apiClient.patch<{
    message: string;
    learning_loop: string;
  }>(`${BASE_URL}/hypotheses/${hypothesisId}/test-results`, update);
  return response.data;
};

/**
 * Get domain pack statistics
 */
export const getDomainPackStatistics = async (
  domainPackId: string
): Promise<DomainPackStatistics> => {
  const response = await apiClient.get<DomainPackStatistics>(
    `${BASE_URL}/${domainPackId}/statistics`
  );
  return response.data;
};

/**
 * Search for domain name suggestions based on query
 * Used for autocomplete functionality
 */
export const searchDomainSuggestions = async (
  query: string
): Promise<string[]> => {
  const response = await apiClient.get<string[]>(
    `${BASE_URL}/search/suggestions`,
    {
      params: { query },
    }
  );
  return response.data;
};

/**
 * Update subdomain metadata (description, scope)
 */
export const updateSubdomain = async (
  domainPackId: string,
  subdomainId: string,
  updates: {
    description?: string;
    scope?: string;
  }
): Promise<{ subdomain_id: string; message: string; subdomain: any }> => {
  const response = await apiClient.patch<{
    subdomain_id: string;
    message: string;
    subdomain: any;
  }>(`${BASE_URL}/${domainPackId}/subdomains/${subdomainId}`, updates);
  return response.data;
};

/**
 * Trigger or re-trigger AI research for an existing subdomain
 */
export const triggerSubdomainResearch = async (
  domainPackId: string,
  subdomainId: string
): Promise<{
  job_id: string;
  subdomain_id: string;
  subdomain_name: string;
  message: string;
  estimated_time: string;
}> => {
  const response = await apiClient.post<{
    job_id: string;
    subdomain_id: string;
    subdomain_name: string;
    message: string;
    estimated_time: string;
  }>(`${BASE_URL}/${domainPackId}/subdomains/${subdomainId}/research`);
  return response.data;
};

/**
 * Get AI-powered subdomain suggestions
 */
export const getSubdomainSuggestions = async (
  domainPackId: string,
  maxSuggestions: number = 5
): Promise<{
  domain_pack_id: string;
  suggestions: Array<{
    name: string;
    description: string;
    scope: string;
    rationale: string;
  }>;
  count: number;
}> => {
  const response = await apiClient.get<{
    domain_pack_id: string;
    suggestions: Array<{
      name: string;
      description: string;
      scope: string;
      rationale: string;
    }>;
    count: number;
  }>(`${BASE_URL}/${domainPackId}/subdomains/suggestions`, {
    params: { max_suggestions: maxSuggestions },
  });
  return response.data;
};

/**
 * Start quality assessment job for domain pack (background processing)
 * Returns job ID immediately for polling
 */
export const assessDomainPackQuality = async (
  domainPackId: string,
  subdomainIds?: string[]
): Promise<{
  jobId: string;
  domainPackId: string;
  domainName: string;
  status: string;
  totalSubdomains: number;
  estimatedDuration: string;
  statusUrl: string;
  message: string;
}> => {
  const response = await apiClient.post<{
    jobId: string;
    domainPackId: string;
    domainName: string;
    status: string;
    totalSubdomains: number;
    estimatedDuration: string;
    statusUrl: string;
    message: string;
  }>(`${BASE_URL}/${domainPackId}/assess`, {
    subdomain_ids: subdomainIds,
  });
  return response.data;
};

/**
 * Get status of a quality assessment job (for polling)
 * Poll every 10-15 seconds until status is 'completed' or 'failed'
 */
export const getAssessmentJobStatus = async (
  domainPackId: string,
  jobId: string
): Promise<{
  jobId: string;
  domainPackId: string;
  domainName: string;
  status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  progress: {
    totalSubdomains: number;
    completedSubdomains: number;
    failedSubdomains: number;
    currentSubdomainIndex: number;
    percentComplete: number;
  };
  subdomainTasks: Array<{
    subdomainId: string;
    subdomainName: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    assessmentId?: string;
    overallScore?: number;
    overallRating?: string;
    error?: string;
  }>;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  results?: {
    overallAssessmentId: string;
    overallScore: number;
    overallRating: string;
  };
  error?: string;
}> => {
  const response = await apiClient.get(
    `${BASE_URL}/${domainPackId}/quality/jobs/${jobId}`
  );
  return response.data;
};

/**
 * Get quality assessment summary for domain pack
 */
export const getDomainPackQuality = async (
  domainPackId: string
): Promise<{
  domain_pack_id: string;
  domain_name: string;
  assessed: boolean;
  overall_score?: number;
  overall_rating?: string;
  total_subdomains: number;
  assessed_subdomains?: number;
  subdomain_breakdown?: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
  total_issues?: number;
  issues_by_severity?: {
    critical: number;
    major: number;
    minor: number;
  };
  subdomain_assessments?: any[];
  message?: string;
}> => {
  const response = await apiClient.get(`${BASE_URL}/${domainPackId}/quality`);
  return response.data;
};

/**
 * Get quality assessment for specific subdomain
 */
export const getSubdomainQuality = async (
  domainPackId: string,
  subdomainId: string
): Promise<any> => {
  const response = await apiClient.get(
    `${BASE_URL}/${domainPackId}/subdomains/${subdomainId}/quality`
  );
  return response.data;
};

/**
 * Resolve or dismiss a quality issue
 */
export const resolveQualityIssue = async (
  issueId: string,
  status: 'resolved' | 'dismissed',
  resolvedBy: string,
  resolutionNotes?: string
): Promise<{
  status: string;
  message: string;
  issue_id: string;
  resolved_by: string;
  resolved_at: string;
}> => {
  const response = await apiClient.patch<{
    status: string;
    message: string;
    issue_id: string;
    resolved_by: string;
    resolved_at: string;
  }>(`/api/quality-issues/${issueId}/resolve`, {
    issue_id: issueId,
    status,
    resolved_by: resolvedBy,
    resolution_notes: resolutionNotes,
  });
  return response.data;
};

/**
 * Start a review for domain pack
 */
export const startReview = async (
  domainPackId: string,
  request: {
    review_mode: 'quick' | 'full' | 'trust_ai';
    reviewer_name: string;
    reviewer_email: string;
    min_score_threshold?: number;
  }
): Promise<any> => {
  const response = await apiClient.post(
    `${BASE_URL}/${domainPackId}/reviews/start`,
    request
  );
  return response.data;
};

/**
 * Get review status
 */
export const getReviewStatus = async (reviewId: string): Promise<any> => {
  const response = await apiClient.get(`${BASE_URL}/reviews/${reviewId}`);
  return response.data;
};

/**
 * Complete Quick Review
 */
export const completeQuickReview = async (
  reviewId: string,
  request: {
    review_id: string;
    outcome: 'approved' | 'flagged' | 'rejected';
    critical_issues_acknowledged: string[];
    quick_notes?: string;
    time_spent_seconds: number;
  }
): Promise<any> => {
  const response = await apiClient.post(
    `${BASE_URL}/reviews/${reviewId}/complete-quick`,
    request
  );
  return response.data;
};

/**
 * Submit subdomain review (Full Review)
 */
export const submitSubdomainReview = async (
  reviewId: string,
  subdomainId: string,
  request: {
    reviewer_overall_score: number;
    dimension_scores: Record<string, number>;
    dimension_feedback: Record<string, string>;
    notes?: string;
    acknowledged_issues: string[];
  }
): Promise<any> => {
  const response = await apiClient.post(
    `${BASE_URL}/reviews/${reviewId}/subdomains/${subdomainId}`,
    {
      subdomain_id: subdomainId,
      ...request,
    }
  );
  return response.data;
};

/**
 * Complete Full Review
 */
export const completeFullReview = async (
  reviewId: string,
  request: {
    review_id: string;
    overall_outcome: 'approved' | 'flagged' | 'rejected';
    strengths: string[];
    weaknesses: string[];
    recommendations: string[];
    overall_notes?: string;
    time_spent_seconds: number;
  }
): Promise<any> => {
  const response = await apiClient.post(
    `${BASE_URL}/reviews/${reviewId}/complete-full`,
    request
  );
  return response.data;
};

/**
 * Approve with Trust AI
 */
export const approveTrustAI = async (
  reviewId: string,
  request: {
    review_id: string;
    confirm: boolean;
  }
): Promise<any> => {
  const response = await apiClient.post(
    `${BASE_URL}/reviews/${reviewId}/trust-ai-approve`,
    request
  );
  return response.data;
};

/**
 * Get all reviews for domain pack
 */
export const getDomainPackReviews = async (domainPackId: string): Promise<any> => {
  const response = await apiClient.get(`${BASE_URL}/${domainPackId}/reviews`);
  return response.data;
};

// ========================================
// Phase 1-3+ APIs (November 2025)
// ========================================

/**
 * List all versions for a domain pack (Phase 1)
 */
export const listDomainPackVersions = async (
  domainPackId: string
): Promise<{
  domain_pack_id: string;
  total_versions: number;
  versions: Array<{
    versionId: string;
    versionNumber: number;
    status: string;
    researchPhase: string;
    qualityScore: number;
    enrichmentStatus: string;
    createdBy: string;
    createdAt: string;
    publishedAt: string | null;
    subdomainCount: number;
  }>;
}> => {
  const response = await apiClient.get(`${BASE_URL}/${domainPackId}/versions`);
  return response.data;
};

/**
 * Get specific version details (Phase 1)
 */
export const getDomainPackVersion = async (
  domainPackId: string,
  versionId: string
): Promise<any> => {
  const response = await apiClient.get(`${BASE_URL}/${domainPackId}/versions/${versionId}`);
  return response.data;
};

/**
 * List industry variants for domain pack (Phase 2)
 */
export const listIndustryVariants = async (
  domainPackId: string
): Promise<Array<{
  variantId: string;
  industry: string;
  variantName: string;
  versionId: string;
  emphasis: string;
  createdAt: string;
}>> => {
  const response = await apiClient.get(`${BASE_URL}/${domainPackId}/industry-variants`);
  return response.data;
};

/**
 * Get specific industry variant (Phase 2)
 */
export const getIndustryVariant = async (
  domainPackId: string,
  variantId: string
): Promise<any> => {
  const response = await apiClient.get(
    `${BASE_URL}/${domainPackId}/industry-variants/${variantId}`
  );
  return response.data;
};

/**
 * Update industry variant metadata (Phase 2)
 */
export const updateIndustryVariant = async (
  domainPackId: string,
  variantId: string,
  updates: {
    industryKPIs?: string[];
    industryChallenges?: string[];
    industryOpportunities?: string[];
    benchmarks?: Record<string, any>;
  }
): Promise<any> => {
  const response = await apiClient.patch(
    `${BASE_URL}/${domainPackId}/industry-variants/${variantId}`,
    updates
  );
  return response.data;
};

/**
 * Get AI-powered domain suggestions based on company context (Phase 3+)
 */
export const suggestDomains = async (request: {
  company_name: string;
  industry: string;
  executive_questions: string[];
  personas?: string[];
  business_unit?: string;
  top_k?: number;
}): Promise<
  Array<{
    domain_pack_id: string | null;
    version_id: string | null;
    domain_name: string;
    industry: string;
    industry_display: string;
    research_available: boolean;
    variant_id: string | null;
    relevance_score: number;
    reasoning: string;
    total_subdomains: number;
    total_hypotheses: number;
    quality_score: number;
    relevant_subdomains: Array<any>;
    top_hypotheses: Array<any>;
  }>
> => {
  const response = await apiClient.post(`${BASE_URL}/suggest-domains`, request);
  return response.data;
};

/**
 * Get AI-powered subdomain suggestions for a domain (without needing existing domain pack)
 * Used in Smart Wizard before domain pack creation
 */
export const suggestSubdomains = async (
  domain_name: string,
  industry: string,
  max_suggestions: number = 5
): Promise<{
  domain_name: string;
  industry: string;
  suggestions: Array<{
    name: string;
    description: string;
    scope: string;
    rationale: string;
  }>;
  count: number;
}> => {
  const response = await apiClient.post(
    `${BASE_URL}/suggest-subdomains?domain_name=${encodeURIComponent(domain_name)}&industry=${encodeURIComponent(industry)}&max_suggestions=${max_suggestions}`
  );
  return response.data;
};

// ========================================
// Research Library Admin APIs (Phase 3+)
// ========================================

/**
 * Get research library health metrics
 */
export const getLibraryHealth = async (): Promise<{
  health_score: number;
  coverage_score: number;
  quality_score: number;
  total_coverage: string;
  recommendations: string[];
  last_updated: string;
}> => {
  const response = await apiClient.get('/research-library/health');
  return response.data;
};

/**
 * Get research library coverage analysis
 */
export const getLibraryCoverage = async (): Promise<{
  total_domain_packs: number;
  total_versions: number;
  total_variants: number;
  industries: Array<{
    industry: string;
    industry_display: string;
    total_domains: number;
    total_variants: number;
    coverage_percentage: number;
    missing_domains: string[];
  }>;
  domains: Array<{
    domain_name: string;
    domain_pack_id: string | null;
    total_industries: number;
    industries_covered: string[];
    industries_missing: string[];
    latest_version: string | null;
    quality_score: number;
  }>;
  top_gaps: Array<{
    domain_name: string;
    industry: string;
    industry_display: string;
    priority: string;
    estimated_time: string;
  }>;
}> => {
  const response = await apiClient.get('/research-library/coverage');
  return response.data;
};

/**
 * Get priority configuration
 */
export const getPriorityConfig = async (): Promise<{
  priority_domains: string[];
  priority_industries: string[];
  total_possible_combinations: number;
  note: string;
}> => {
  const response = await apiClient.get('/research-library/priority-config');
  return response.data;
};

/**
 * Trigger batch research for multiple domain-industry combinations
 */
export const triggerBatchResearch = async (
  pairs: Array<{
    domain_name: string;
    industry: string;
  }>,
  created_by: string = 'admin'
): Promise<{
  total_combinations: number;
  started: number;
  failed: number;
  jobs: Array<any>;
}> => {
  const response = await apiClient.post(
    `/research-library/batch-research?created_by=${created_by}`,
    pairs
  );
  return response.data;
};

/**
 * Automatically fill top N priority gaps
 */
export const fillPriorityGaps = async (
  top_n: number = 10,
  created_by: string = 'admin'
): Promise<{
  total_combinations: number;
  started: number;
  failed: number;
  jobs: Array<any>;
  message: string;
}> => {
  const response = await apiClient.post(
    `/research-library/fill-gaps?top_n=${top_n}&created_by=${created_by}`
  );
  return response.data;
};

/**
 * Get combined library statistics
 */
export const getLibraryStats = async (): Promise<{
  coverage: any;
  health: any;
  priority_config: any;
}> => {
  const response = await apiClient.get('/research-library/stats');
  return response.data;
};

// ============================================
// Research Item Relationships (Phase 7)
// ============================================

export interface ResearchRelationship {
  relationshipId: string;
  relationshipType: string;
  strength: string;
  explanation: string;
  confidence: number;
  sourceType: string;
  sourceId: string;
  sourceTitle: string;
  targetType: string;
  targetId: string;
  targetTitle: string;
}

export interface SubdomainRelationshipsResponse {
  subdomain_id: string;
  domain_pack_id: string;
  relationships: ResearchRelationship[];
  summary: {
    total: number;
    by_type: Record<string, number>;
  };
}

export interface ItemRelationshipsResponse {
  item_id: string;
  item_type: string;
  domain_pack_id: string;
  outgoing: ResearchRelationship[];
  incoming: ResearchRelationship[];
}

/**
 * Get all relationships within a subdomain
 */
export const getSubdomainRelationships = async (
  domainPackId: string,
  subdomainId: string,
  relationshipType?: string
): Promise<SubdomainRelationshipsResponse> => {
  const params = new URLSearchParams();
  if (relationshipType) params.append('relationship_type', relationshipType);

  const url = `${BASE_URL}/${domainPackId}/subdomains/${subdomainId}/relationships${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await apiClient.get<SubdomainRelationshipsResponse>(url);
  return response.data;
};

/**
 * Get relationships for a specific item
 */
export const getItemRelationships = async (
  domainPackId: string,
  itemType: string,
  itemId: string,
  direction: 'outgoing' | 'incoming' | 'both' = 'both'
): Promise<ItemRelationshipsResponse> => {
  const response = await apiClient.get<ItemRelationshipsResponse>(
    `${BASE_URL}/${domainPackId}/items/${itemType}/${itemId}/relationships?direction=${direction}`
  );
  return response.data;
};

/**
 * Get issues with their solutions (best practices that address them)
 */
export const getIssuesWithSolutions = async (
  domainPackId: string,
  subdomainId: string
): Promise<{
  subdomain_id: string;
  domain_pack_id: string;
  issues: Array<{
    issueId: string;
    title: string;
    description: string;
    severity: string;
    businessImpact: string;
    solutions: Array<{
      practiceId: string;
      title: string;
      description: string;
      complexity: string;
      relationship: {
        strength: string;
        explanation: string;
      };
    }>;
  }>;
  summary: {
    total_issues: number;
    issues_with_solutions: number;
    issues_without_solutions: number;
  };
}> => {
  const response = await apiClient.get(
    `${BASE_URL}/${domainPackId}/subdomains/${subdomainId}/issues-with-solutions`
  );
  return response.data;
};

/**
 * Trigger relationship discovery for a subdomain
 */
export const triggerRelationshipDiscovery = async (
  domainPackId: string,
  subdomainId: string,
  force: boolean = false
): Promise<{
  job_id: string;
  subdomain_id: string;
  domain_pack_id: string;
  status: string;
  message: string;
  deleted_existing: boolean;
}> => {
  const response = await apiClient.post(
    `${BASE_URL}/${domainPackId}/subdomains/${subdomainId}/discover-relationships?force=${force}`
  );
  return response.data;
};
